// src/pages/TextesReglementaires/MentionsLegalesPage.jsx

import React, { useEffect, useMemo, useState } from "react";

import useScrollToTop from "../../hooks/useScollToTop";
import { getSettings } from "../../services/settingsServices";

import LegalPageLayout from "./components/LegalPageLayout";
import {
  formatLegalDate,
  getMairieIdentity,
  getSettingsRecord,
} from "./utils/legalPagesUtils";

function getTechnicalProvider(settings = {}) {
  return {
    name:
      settings.webmaster_nom ||
      settings.nom_webmaster ||
      settings.prestataire_nom ||
      settings.technical_provider_name ||
      "Prestataire technique à renseigner",
    email:
      settings.webmaster_email ||
      settings.mail_webmaster ||
      settings.prestataire_email ||
      settings.technical_provider_email ||
      "",
    website:
      settings.webmaster_site ||
      settings.prestataire_site ||
      settings.technical_provider_website ||
      "",
  };
}

export default function MentionsLegalesPage() {
  useScrollToTop();

  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);

      try {
        const data = await getSettings();

        if (mounted) {
          setSettings(getSettingsRecord(data));
        }
      } catch (error) {
        console.error("Erreur chargement settings mentions légales:", error);

        if (mounted) {
          setSettings({});
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const identity = useMemo(() => getMairieIdentity(settings), [settings]);
  const technicalProvider = useMemo(
    () => getTechnicalProvider(settings),
    [settings]
  );

  const updatedAt = formatLegalDate(settings.updated_at);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1240px] px-4 pb-16 pt-32 sm:px-6">
        <div className="animate-pulse space-y-5">
          <div className="h-8 w-72 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-5 w-full max-w-2xl rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-96 rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <LegalPageLayout
      title="Mentions légales"
      eyebrow="Textes réglementaires"
      description="Informations légales relatives à l’éditeur, à la publication, à l’hébergement et à l’utilisation du site internet officiel de la mairie."
      identity={identity}
      updatedAt={updatedAt}
    >
      <h2>1. Éditeur du site</h2>

      <p>
        Le présent site internet <strong>{identity.siteUrl}</strong> est édité
        par <strong>{identity.name}</strong>, collectivité territoriale.
      </p>

      <ul>
        <li>
          <strong>Nom de la collectivité :</strong> {identity.name}
        </li>

        {identity.address && (
          <li>
            <strong>Adresse :</strong>{" "}
            <span className="whitespace-pre-line">{identity.address}</span>
          </li>
        )}

        {identity.phone && (
          <li>
            <strong>Téléphone :</strong>{" "}
            <a href={`tel:${String(identity.phone).replace(/\s+/g, "")}`}>
              {identity.phone}
            </a>
          </li>
        )}

        {identity.email && (
          <li>
            <strong>Adresse e-mail :</strong>{" "}
            <a href={`mailto:${identity.email}`}>{identity.email}</a>
          </li>
        )}

        {identity.siret && (
          <li>
            <strong>Numéro SIRET :</strong> {identity.siret}
          </li>
        )}
      </ul>

      <h2>2. Directeur de la publication</h2>

      <p>
        Le directeur de la publication du site est{" "}
        <strong>{identity.publicationDirector}</strong>, en qualité de
        représentant légal de la collectivité, sauf mention contraire définie par
        la mairie.
      </p>

      <p>
        La mairie veille à la qualité des informations publiées sur le site.
        Toutefois, les informations administratives, horaires, démarches,
        actualités et documents peuvent évoluer. Les usagers sont invités à
        contacter directement les services municipaux en cas de doute.
      </p>

      <h2>3. Responsable éditorial</h2>

      <p>
        Le contenu éditorial du site est placé sous la responsabilité de{" "}
        <strong>{identity.name}</strong>. Les services municipaux peuvent être
        amenés à contribuer à la rédaction, à la mise à jour ou à la publication
        des informations relevant de leurs compétences.
      </p>

      <h2>4. Conception, développement et maintenance technique</h2>

      <p>
        La conception, le développement ou la maintenance technique du site
        peuvent être assurés par un prestataire ou un service technique désigné
        par la commune.
      </p>

      <ul>
        <li>
          <strong>Prestataire / webmaster :</strong> {technicalProvider.name}
        </li>

        {technicalProvider.email && (
          <li>
            <strong>Contact technique :</strong>{" "}
            <a href={`mailto:${technicalProvider.email}`}>
              {technicalProvider.email}
            </a>
          </li>
        )}

        {technicalProvider.website && (
          <li>
            <strong>Site du prestataire :</strong>{" "}
            <a
              href={technicalProvider.website}
              target="_blank"
              rel="noreferrer"
            >
              {technicalProvider.website}
            </a>
          </li>
        )}
      </ul>

      <h2>5. Hébergement du site</h2>

      <p>Le site est hébergé par :</p>

      <ul>
        <li>
          <strong>Hébergeur :</strong> {identity.hostName}
        </li>
        <li>
          <strong>Adresse de l’hébergeur :</strong> {identity.hostAddress}
        </li>
      </ul>

      <p>
        Les informations relatives à l’hébergement doivent être adaptées aux
        contrats effectivement souscrits par la mairie ou par son prestataire
        technique.
      </p>

      <h2>6. Objet du site</h2>

      <p>
        Le site internet de <strong>{identity.name}</strong> a pour objet de
        présenter les informations publiques de la commune : actualités, agenda,
        services municipaux, démarches, publications, documents publics, projets,
        vie associative, commerces, informations pratiques et contenus
        institutionnels.
      </p>

      <p>
        Les informations publiées ont une vocation informative. Elles ne se
        substituent pas aux actes administratifs officiels, aux délibérations,
        aux arrêtés ou aux documents régulièrement publiés par la collectivité.
      </p>

      <h2>7. Propriété intellectuelle</h2>

      <p>
        Les contenus présents sur le site, notamment les textes, images,
        photographies, documents, logos, éléments graphiques, vidéos, fichiers
        téléchargeables et mises en page, sont protégés par le droit de la
        propriété intellectuelle, sauf mention contraire.
      </p>

      <p>
        Toute reproduction, représentation, modification, diffusion ou
        réutilisation totale ou partielle des contenus doit respecter les droits
        de <strong>{identity.name}</strong>, de ses partenaires et des éventuels
        titulaires de droits.
      </p>

      <p>
        Les documents administratifs publiés peuvent être soumis à des règles
        spécifiques de réutilisation des informations publiques. En cas de doute,
        l’usager est invité à contacter la mairie.
      </p>

      <h2>8. Crédits photographiques et contenus tiers</h2>

      <p>
        Les photographies, illustrations, vidéos ou contenus provenant de tiers
        restent la propriété de leurs auteurs ou ayants droit respectifs.
      </p>

      <p>
        Lorsque des crédits spécifiques sont nécessaires, ils peuvent être
        indiqués directement au sein des pages concernées, dans les documents
        associés ou dans les supports de communication de la mairie.
      </p>

      <h2>9. Liens hypertextes</h2>

      <p>
        Le site peut contenir des liens vers des sites internet externes :
        services publics, plateformes administratives, partenaires
        institutionnels, associations, commerces ou organismes tiers.
      </p>

      <p>
        <strong>{identity.name}</strong> ne peut être tenue responsable du
        contenu, de la disponibilité, de la sécurité ou des pratiques de ces sites
        externes. L’utilisateur est invité à consulter leurs propres conditions
        d’utilisation et politiques de confidentialité.
      </p>

      <h2>10. Responsabilité</h2>

      <p>
        La mairie s’efforce de fournir des informations exactes, accessibles et
        régulièrement mises à jour. Toutefois, elle ne garantit pas l’absence
        d’erreurs, d’omissions, d’inexactitudes ou d’interruptions temporaires du
        site.
      </p>

      <p>
        <strong>{identity.name}</strong> ne saurait être tenue responsable des
        dommages directs ou indirects résultant de l’utilisation du site, d’une
        interruption de service, d’un problème technique, d’un défaut
        d’actualisation ou de l’utilisation des informations publiées.
      </p>

      <h2>11. Données personnelles</h2>

      <p>
        La consultation du site peut entraîner le traitement de certaines données
        personnelles, notamment lors de l’utilisation de formulaires de contact,
        de demandes en ligne, d’inscriptions à des services ou de communications
        avec la mairie.
      </p>

      <p>
        Les traitements de données personnelles mis en œuvre par la commune sont
        décrits dans la politique de confidentialité et la page relative au RGPD.
        Les usagers disposent notamment de droits d’accès, de rectification,
        d’opposition, d’effacement, de limitation et, lorsque cela s’applique, de
        portabilité.
      </p>

      <p>
        Pour toute demande relative à vos données personnelles, vous pouvez
        contacter la mairie
        {identity.dpoEmail ? (
          <>
            {" "}
            à l’adresse suivante :{" "}
            <a href={`mailto:${identity.dpoEmail}`}>{identity.dpoEmail}</a>.
          </>
        ) : (
          "."
        )}
      </p>

      <h2>12. Cookies et traceurs</h2>

      <p>
        Le site peut utiliser des cookies ou traceurs strictement nécessaires à
        son fonctionnement, ainsi que, le cas échéant, des outils de mesure
        d’audience, d’intégration de contenus externes ou de services tiers.
      </p>

      <p>
        Lorsque le consentement de l’utilisateur est requis, celui-ci doit pouvoir
        accepter, refuser ou modifier ses choix selon les modalités prévues sur
        le site.
      </p>

      <h2>13. Accessibilité</h2>

      <p>
        La mairie s’efforce de rendre son site internet accessible au plus grand
        nombre, conformément aux principes d’accessibilité numérique applicables
        aux services publics.
      </p>

      <p>
        Les éventuelles informations relatives au niveau de conformité, au schéma
        pluriannuel d’accessibilité ou à la déclaration d’accessibilité peuvent
        être publiées sur une page dédiée.
      </p>

      <h2>14. Contact</h2>

      <p>
        Pour toute question relative aux présentes mentions légales ou au site
        internet, l’utilisateur peut contacter <strong>{identity.name}</strong>.
      </p>

      {identity.email && (
        <p>
          E-mail : <a href={`mailto:${identity.email}`}>{identity.email}</a>
        </p>
      )}

      {identity.phone && (
        <p>
          Téléphone :{" "}
          <a href={`tel:${String(identity.phone).replace(/\s+/g, "")}`}>
            {identity.phone}
          </a>
        </p>
      )}

      {identity.address && (
        <p>
          Adresse :{" "}
          <strong className="whitespace-pre-line">{identity.address}</strong>
        </p>
      )}
    </LegalPageLayout>
  );
}